import { addDays, format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const timeStrings = {
  german: {
    date: 'dd.MM.yyyy',
    time: 'dd.MM.yyyy, HH:mm',
    timeWithSeconds: 'dd.MM.yyyy, HH:mm:ss'
  },
  english: {
    date: 'dd/MM/yyyy',
    time: 'dd/MM/yyyy HH:mm',
    timeWithSeconds: 'dd/MM/yyyy HH:mm:ss'
  }
};

type DateValue = string | Date | undefined;

export const getFormatStringByLanguage = (
  key: 'date' | 'time' | 'timeWithSeconds'
): string => {
  switch (localStorage.getItem('currentLang')) {
    case 'en_US':
      return timeStrings.english[key];
    default:
      return timeStrings.german[key];
  }
};

export const dateFormatWithoutTime = (): string =>
  getFormatStringByLanguage('date');

export const dateFormatter = {
  dateForFileNames: (value: DateValue): string => {
    const stringFormat = 'yyyy-MM-dd';
    const date = value instanceof Date ? value : new Date(`${value}`);
    return format(date, stringFormat);
  },
  dateWithSeconds: (value: DateValue): string => {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(`${value}`);
    if (isNaN(date.getTime())) {
      return date.toString(); // `Invalid Date`
    }
    return format(date, getFormatStringByLanguage('timeWithSeconds'));
  },
  dateWithoutSeconds: (value: DateValue): string => {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(`${value}`);
    if (isNaN(date.getTime())) {
      return date.toString(); // `Invalid Date`
    }
    return format(date, getFormatStringByLanguage('time'));
  },
  dateWithoutTime: (value: DateValue): string => {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(`${value}`);
    if (isNaN(date.getTime())) {
      return date.toString(); // `Invalid Date`
    }
    return format(date, getFormatStringByLanguage('date'));
  },
  dateWithoutSecondsWithoutOffset: (value: string) => {
    if (!value) {
      return '';
    }
    const timeWithoutOffset = value.split(/[+-]\d{2}:\d{2}$|Z$/)[0];
    return formatInTimeZone(
      timeWithoutOffset,
      'UTC',
      getFormatStringByLanguage('time')
    );
  },

  /**
   * Return a date string in raw ISO format with the time set to 01:00:00 UTC, regardless of the local timezone.
   *
   * @param value - date string
   * @returns ISO date string or empty string if value is empty
   *
   * @example
   * const isoDate = dateFormatter.toISOStringAtOneUTC('2025-01-25');
   * console.log(isoDate); // "2025-01-25T01:00:00.000Z"
   */
  toISOStringAtOneUTC: (value: DateValue): string => {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(`${value}`);
    if (isNaN(date.getTime())) {
      return date.toString(); // `Invalid Date`
    }

    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 1)
    ).toISOString();
  },

  /**
   * Adds a specified number of days to a date string and returns the result as an ISO string.
   *
   * Note: This function returns raw ISO format (e.g., "2025-01-25T00:00:00.000Z").
   * For user-facing display, combine with formatting functions like `dateWithoutTime`,
   * `dateWithSeconds`, or `dateWithoutSeconds` defined in this module.
   *
   * @param value - date string
   * @param days - Number of days to add
   * @returns ISO date string or empty string if value is empty
   *
   * @example
   * const futureDate = dateFormatter.addDaysToDate('2025-01-20', 5);
   * const formatted = dateFormatter.dateWithoutTime(futureDate); // "25.01.2025" (de_DE)
   */
  addDaysToDate: (value: string, days: number): string =>
    value ? addDays(value, days).toISOString() : '',

  /**
   * Converts a number of seconds to a time string format.
   *
   * @param seconds - The number of seconds to convert
   * @returns A formatted time string in the format "\{minutes\}m\{seconds\}s"
   *
   * @example
   * dateFormatter.secondsToTime(90); // "1m30s"
   * dateFormatter.secondsToTime(45); // "0m45s"
   */
  secondsToTime: (seconds: number): string => {
    const SECONDS_IN_MINUTE = 60;
    const quotient = Math.floor(seconds / SECONDS_IN_MINUTE);
    const remainder = seconds % SECONDS_IN_MINUTE;
    return `${quotient}m${remainder}s`;
  }
} as const;
